import { watch } from 'vue'
import type { Router } from 'vue-router'
import type { PreviewItem } from '@/components/file-preview'
import { pushBackHandler } from '@/native/backStack'
import { artifactRoute, artifactSurface, type ArtifactRef } from './artifacts'
import type { AgentOverlays } from './overlays'
import type { AgentWorkspace } from './workspace'

/**
 * Phones: something full-screen (an overlay, the file previewer) is opening over the workspace sheet. Tuck the
 * sheet away first, so nothing opened from it ever renders behind it, and bring it back where it was once the
 * cover closes, unless the creator moved on meanwhile (restored or closed the workspace themselves).
 * `covering` is read reactively; the watch ends once it turns false.
 */
export function stepAside(ws: AgentWorkspace | null | undefined, covering: () => boolean): void {
    if (!ws?.sheet || !ws.presented) return
    ws.minimize()
    const stop = watch(covering, (on) => {
        if (on) return
        stop()
        if (ws.minimized) ws.restore()
    }, { flush: 'post' })
}

/**
 * Open the shared file previewer from the workspace (a file chip, an attachment, an invoice PDF). On a phone the
 * sheet steps aside while it's up (and comes back after), and Android back closes the previewer.
 */
export async function previewFromWorkspace(items: PreviewItem | PreviewItem[], opts: { index?: number } = {}, ws?: AgentWorkspace | null): Promise<void> {
    const fp = await import('@/components/file-preview')
    fp.openFilePreview(items, opts)
    if (!ws?.sheet || !ws.presented) return
    const preview = fp.useFilePreview()
    stepAside(ws, () => preview.isOpen.value)
    const off = pushBackHandler(() => { fp.closeFilePreview() }, { priority: 50 })
    const stop = watch(() => preview.isOpen.value, (on) => {
        if (on) return
        off()
        stop()
    })
}

/**
 * Open files in the shared full-screen previewer (their signed links are fetched when it opens). A group opens as one
 * set to flip through; a single file opens on its own. Loaded on first use so the conversation stays light.
 */
async function openInPreviewer(a: ArtifactRef, ws?: AgentWorkspace | null): Promise<void> {
    const [{ dealFileItem }, { default: useUser }] = await Promise.all([
        import('@/components/file-preview'),
        import('@/composables/useUser'),
    ])
    const orgId = useUser().user.value?.organization?.id
    const item = (f: ArtifactRef) => {
        const d = f.data ?? {}
        return dealFileItem({
            id: f.id,
            name: String(d.name ?? f.title ?? 'File'),
            url: (d.url as string | undefined) || null,
            external_url: (d.external_url as string | undefined) || null,
            mime: (d.content_type ?? d.mime ?? d.type ?? null) as string | null,
            size: (d.size as number | undefined) ?? null,
        }, orgId)
    }
    await previewFromWorkspace(a.items ? a.items.map(item) : item(a), {}, ws)
}

/**
 * Open an artifact where it belongs (see `artifactSurface`). With no pane or overlay to open in (a surface
 * without a workspace), fall back to the record's page, except for things that open nothing.
 *
 * `inside`: opened from inside the workspace (a row in a group list, a link in a viewer). Pane records are then
 * pushed over the record on screen (`ws.push`: a back stack on phones, a tab elsewhere), which also lets a
 * single email draft from a "3 drafts" list open in the pane.
 */
export function openArtifact(
    a: ArtifactRef,
    where: { ws?: AgentWorkspace | null; overlays?: AgentOverlays | null; router?: Router | null; inside?: boolean },
): void {
    const surface = artifactSurface(a)
    if (surface === 'none') return
    if (surface === 'previewer') {
        void openInPreviewer(a, where.ws)
        return
    }
    // The overlay host tucks a phone sheet away while an overlay is up (whoever opened it).
    if (surface === 'overlay' && where.overlays) return where.overlays.open(a)
    if (surface === 'pane' && where.ws) return where.inside ? where.ws.push(a) : where.ws.open(a)
    const path = artifactRoute(a)
    if (path) void where.router?.push(path)
}
